// src/views/student/ActivityModal/ActivityModal.tsx
// Okno „Aktywność” z ekranu „Uczniowie”: trzy liczby za ostatnie 14 dni, pasek tych dni (słupek = minuty dnia),
// a niżej dni, w których uczeń ćwiczył, każdy z listami — pod listą jej gry
import React, { FC, useEffect, useState } from 'react';
import { Spin } from 'antd';
import classNames from 'classnames';
import { TbInfoCircle } from 'react-icons/tb';
import { useTranslation } from 'react-i18next';
import i18n, { getDateLocale } from '../../../i18n';
import AppModal from '../../../components/AppModal';
import { ACTIVITY_GAME_PATHS, GAMES, GameGroup } from '../../../constants/Games';
import ApiService from '../../../services/ApiService';
import {
  ActivityTotals, StudentActivityDay, StudentActivityGame, UserData,
} from '../../../services/ApiService.types';
import { formatActiveMinutes, formatCorrectPercent } from '../../../utils/activityFormat';
import { getApiErrorMessage } from '../../../utils/apiErrors';
import { pluralForm } from '../../../utils/plural';
import classes from './ActivityModal.module.less';

// Czas od ostatniej lekcji z zapasem — wystarcza też przy lekcjach co dwa tygodnie. Backend trzyma dane 30 dni
const DAYS = 14;

// Szerzej niż okienka `wide` (540px): obok kolumn z liczbami nazwa listy ma ~315px zamiast ~215px. Na telefonie okno
// i tak zajmuje cały ekran
const MODAL_WIDTH = 640;

// Najniższy słupek dnia z ćwiczeniami, w procentach wysokości paska — kilka sekund pracy też ma być widać
const MIN_BAR_PERCENT = 12;

// Gra w danych po pozycji w menu — odwrotnie niż ACTIVITY_GAME_PATHS
const ACTIVITY_GAME_BY_PATH = new Map(
  Object.entries(ACTIVITY_GAME_PATHS).map(([game, path]): [string, string] => [path, game]),
);

// Ta sama gra w obu grupach ma tę samą nazwę, w Easy z końcówką: `find-easy` to Znajdź, tak jak `find`
const EASY_SUFFIX = '-easy';

// Dzisiejsza data według czasu polskiego, tak jak doby liczy backend. Zapis szwedzki to właśnie YYYY-MM-DD
const getPolishToday = (): string => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Warsaw' }).format(new Date());

// Data YYYY-MM-DD przesunięta o `days` dni
const shiftDate = (date: string, days: number): string => {
  const shifted = new Date(`${date}T00:00:00Z`);

  shifted.setUTCDate(shifted.getUTCDate() + days);

  return shifted.toISOString().slice(0, 10);
};

// Dzień tygodnia w języku interfejsu: „pon.” albo sama litera pod słupkiem („P”)
const formatWeekday = (date: string, weekday: 'short' | 'narrow'): string => (
  new Date(`${date}T00:00:00Z`).toLocaleDateString(getDateLocale(), { weekday, timeZone: 'UTC' })
);

// „27.10”
const formatShortDate = (date: string): string => `${date.slice(8, 10)}.${date.slice(5, 7)}`;

// „pon. 27.10”
const formatDay = (date: string): string => `${formatWeekday(date, 'short')} ${formatShortDate(date)}`;

// „1 odpowiedź”, „2 odpowiedzi”, „5 odpowiedzi”
const formatAnswers = (count: number): string => i18n.t(`student:activity.answers.${pluralForm(count)}`, { n: count });

// Sumy całego okresu z sum jego dni
const sumDays = (days: StudentActivityDay[]): ActivityTotals => days.reduce(
  (sum, day) => ({
    activeSeconds: sum.activeSeconds + day.activeSeconds,
    answers: sum.answers + day.answers,
    checked: sum.checked + day.checked,
    correct: sum.correct + day.correct,
  }),
  {
    activeSeconds: 0, answers: 0, checked: 0, correct: 0,
  },
);

const isEasyGame = (game: string): boolean => game.endsWith(EASY_SUFFIX);

// Nazwa gry bez grupy — klucz, pod którym ta sama gra z obu grup trafia do jednego kwadracika
const sameGame = (game: string): string => (isEasyGame(game) ? game.slice(0, -EASY_SUFFIX.length) : game);

// Suma dwóch sum: ta sama gra z obu grup na jednej liście
const addTotals = (a: ActivityTotals, b: ActivityTotals): ActivityTotals => ({
  activeSeconds: a.activeSeconds + b.activeSeconds,
  answers: a.answers + b.answers,
  checked: a.checked + b.checked,
  correct: a.correct + b.correct,
});

// Podpowiedź kwadracika gry, jak wiersz listy: „3 min · 24 odpowiedzi · 75%”. Bez odpowiedzi (Powtórz) i bez odsetka
// (gry, które nie sprawdzają odpowiedzi) — krócej
const formatGameTotals = (totals: ActivityTotals): string => [
  formatActiveMinutes(totals.activeSeconds),
  totals.answers > 0 ? formatAnswers(totals.answers) : null,
  formatCorrectPercent(totals),
].filter(Boolean).join(' · ');

type ActivitySummaryProps = {
  days: StudentActivityDay[];
  from: string;
  to: string;
};

// Trzy liczby za cały okres i pasek jego dni: słupek wysoki jak minuty dnia na tle najdłuższego, dzień bez ćwiczeń —
// kreska, ostatni to dziś. Dymek nad słupkiem podaje dzień i minuty
const ActivitySummary: FC<ActivitySummaryProps> = ({ days, from, to }) => {
  const { t } = useTranslation();
  const totals = sumDays(days);
  // Bez ćwiczeń „0 min”, nie „<1 min”
  const minutes = totals.activeSeconds > 0 ? formatActiveMinutes(totals.activeSeconds) : t('student:activity.minutes', { n: 0 });
  const percent = formatCorrectPercent(totals);
  const daysByDate = new Map(days.map((day) => [day.date, day]));
  const maxSeconds = Math.max(...days.map((day) => day.activeSeconds), 1);
  const dates = Array.from({ length: DAYS }, (_, index) => shiftDate(from, index));

  return (
    <>
      <div className={classes.tiles}>
        <div className={classes.tile}>
          <span className={classes.tileLabel}>{t('student:activity.workTime')}</span>
          <span className={classes.tileValue}>{minutes}</span>
        </div>
        <div className={classes.tile}>
          <span className={classes.tileLabel}>{t('student:activity.practiceDays')}</span>
          <span className={classes.tileValue}>{t('student:activity.daysOf', { n: days.length, total: DAYS })}</span>
        </div>
        <div className={classes.tile}>
          <span className={classes.tileLabel}>{t('student:activity.correct')}</span>
          <span className={classes.tileValue}>{percent ?? '—'}</span>
        </div>
      </div>

      <div className={classes.strip}>
        <div className={classes.bars}>
          {dates.map((date) => {
            const day = daysByDate.get(date);

            return (
              <span
                key={date}
                className={classNames(classes.bar, { [classes.barOn]: !!day })}
                style={day ? { height: `${Math.max((day.activeSeconds / maxSeconds) * 100, MIN_BAR_PERCENT)}%` } : undefined}
                title={`${formatDay(date)} — ${day ? formatActiveMinutes(day.activeSeconds) : t('student:activity.noPractice')}`}
              />
            );
          })}
        </div>
        <div className={classes.weekdays}>
          {dates.map((date) => (
            <span key={date} className={classNames(classes.weekday, { [classes.today]: date === to })}>
              {formatWeekday(date, 'narrow')}
            </span>
          ))}
        </div>
      </div>
    </>
  );
};

type ActivityGamesProps = {
  games: StudentActivityGame[];
};

// Pasek gier listy pod jej wierszem: kwadraciki w kolejności menu, z numerem gry (Literuj, spoza kolejki, z napisem jak
// w menu), morskie dla gier, w które uczeń grał tego dnia. W każdym wierszu stoją w tym samym miejscu, więc ta sama gra
// wypada w kolumnie w dół okna. Układ menu Easy, gdy wśród gier jest choć jedna Easy: zestaw Easy obejmuje wszystkie
// gry Regular, więc gra z drugiej grupy (uczeń przełączył grupę albo grał na dwóch urządzeniach) zapala kwadracik tej
// samej gry i dodaje się do jej sum. Gra o nazwie, której okno nie zna, nie ma kwadracika
const ActivityGames: FC<ActivityGamesProps> = ({ games }) => {
  const { t } = useTranslation();
  const group = games.some(({ game }) => isEasyGame(game)) ? GameGroup.KIDDO : GameGroup.OLDER;
  const totalsByGame = games.reduce((byGame, { game, ...totals }) => {
    const sum = byGame.get(sameGame(game));

    return byGame.set(sameGame(game), sum ? addTotals(sum, totals) : totals);
  }, new Map<string, ActivityTotals>());

  return (
    <div className={classes.games}>
      {GAMES[group].map((game) => {
        const activityGame = ACTIVITY_GAME_BY_PATH.get(game.path);
        const totals = activityGame ? totalsByGame.get(sameGame(activityGame)) : undefined;
        const name = game.number ? `${game.number}. ${t(game.text)}` : t(game.text);

        return (
          <span
            key={game.path}
            className={classNames(classes.game, { [classes.gamePlayed]: !!totals, [classes.gameSpell]: !game.number })}
            title={`${name} — ${totals ? formatGameTotals(totals) : t('student:activity.noPractice')}`}
          >
            {game.number ?? (game.label && t(game.label))}
          </span>
        );
      })}
    </div>
  );
};

type ActivityRowProps = {
  label: string;
  totals: ActivityTotals;
  isDay?: boolean;
  // Gry listy — pasek pod jej wierszem. Bez nich (dzień albo starszy backend) wiersz jest bez paska
  games?: StudentActivityGame[];
};

// Wiersz dnia albo listy: minuty, odpowiedzi i odsetek poprawnych w plakietce („—” bez odpowiedzi z gier
// sprawdzających), a pod listą jej gry
const ActivityRow: FC<ActivityRowProps> = ({
  label, totals, isDay, games,
}) => {
  const percent = formatCorrectPercent(totals);

  return (
    <div className={classNames(classes.row, isDay ? classes.dayRow : classes.listRow)}>
      <span className={classes.label} title={label}>{label}</span>
      <span className={classes.number}>{formatActiveMinutes(totals.activeSeconds)}</span>
      <span className={classes.number}>{formatAnswers(totals.answers)}</span>
      <span className={classNames(classes.percent, { [classes.noPercent]: !percent })}>{percent ?? '—'}</span>
      {games && <ActivityGames games={games} />}
    </div>
  );
};

type ActivityModalProps = {
  student: UserData;
  hideModal: () => void;
};

const ActivityModal: FC<ActivityModalProps> = ({ student, hideModal }) => {
  const { t } = useTranslation();
  const deletedListLabel = t('student:activity.deletedList');
  // Okres ustalony przy otwarciu okna: DAYS dni to dziś i DAYS − 1 poprzednich — backend wlicza oba dni graniczne
  const [to] = useState(getPolishToday);
  const from = shiftDate(to, -(DAYS - 1));
  // `null` — jeszcze się wczytują
  const [days, setDays] = useState<StudentActivityDay[] | null>(null);
  // Tekst serwera przy odpowiedzi z kodem (np. `STUDENT_NOT_FOUND` — uczeń usunięty w innej karcie), inaczej ogólny
  const [errorText, setErrorText] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    ApiService.students.activityDays(student.id, from, to)
      .then(({ data }) => {
        if (!cancelled) setDays([...data.days].sort((a, b) => b.date.localeCompare(a.date)));
      })
      .catch((error) => {
        if (!cancelled) {
          setErrorText(getApiErrorMessage(error, i18n.t('student:activity.loadError')));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [student.id, from, to]);

  return (
    <AppModal title={t('student:activity.title')} onCancel={hideModal} width={MODAL_WIDTH}>
      <div className={classes.content}>
        {/* Inicjały jak w awatarze na liście uczniów */}
        <div className={classes.student}>
          <span className={classes.avatar}>{student.name[0].toUpperCase()}{student.surname[0].toUpperCase()}</span>
          <span className={classes.studentName}>{student.name} {student.surname}</span>
          <span className={classes.period}>{formatShortDate(from)} – {formatShortDate(to)}</span>
        </div>

        {errorText && <p className={classes.empty}>{errorText}</p>}

        {!errorText && !days && (
          <div className={classes.loading}>
            <Spin size="small" />
          </div>
        )}

        {days && (
          <>
            <ActivitySummary days={days} from={from} to={to} />

            {days.length > 0 ? (
              <div className={classes.days}>
                {days.map((day) => (
                  <div key={day.date} className={classes.day}>
                    <ActivityRow label={formatDay(day.date)} totals={day} isDay />
                    {day.lists.map((list, index) => (
                      <ActivityRow
                        // Usuniętych list w jednym dniu może być kilka, wszystkie z `listId` równym `null`
                        // eslint-disable-next-line react/no-array-index-key
                        key={list.listId ?? `deleted-${index}`}
                        label={list.name ?? deletedListLabel}
                        totals={list}
                        games={list.games}
                      />
                    ))}
                  </div>
                ))}
              </div>
            ) : (
              <p className={classes.empty}>{t('student:activity.emptyPeriod', { n: DAYS })}</p>
            )}
          </>
        )}

        <p className={classes.rules}>
          <TbInfoCircle className={classes.rulesIcon} />
          <span>{t('student:activity.lastDays', { n: DAYS })} {t('student:activity.rules')}</span>
        </p>
      </div>
    </AppModal>
  );
};

export default ActivityModal;
